import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/http';
import { ApiError } from '@/features/auth/api';

export interface Session {
  id: string;
  current: boolean;
  ip: string | null;
  userAgent: string | null;
  lastSeenAt: string; // ISO date-time
  createdAt: string;
  expiresAt: string;
}

export interface BulkCloseResult {
  closed: number;
}

/**
 * Whose sessions are being managed: the signed-in user (`'me'`) or, for the admin
 * panel, a specific user. Drives both the request URL and the React-Query cache key.
 */
export type SessionScope = 'me' | { userId: number };

function basePath(scope: SessionScope): string {
  return scope === 'me'
    ? '/api/me/sessions'
    : `/api/admin/users/${scope.userId}/sessions`;
}

export function sessionsQueryKey(scope: SessionScope): readonly (string | number)[] {
  return scope === 'me' ? ['sessions', 'me'] : ['sessions', 'admin', scope.userId];
}

export function useSessions(scope: SessionScope) {
  return useQuery<Session[]>({
    queryKey: sessionsQueryKey(scope),
    queryFn: () => request<Session[]>(basePath(scope)),
    staleTime: 30_000,
  });
}

export function useCloseSession(scope: SessionScope) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id: string) =>
      request<void>(`${basePath(scope)}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionsQueryKey(scope) }),
  });
}

export function useCloseSessions(scope: SessionScope, options?: { keepCurrent?: boolean }) {
  const qc = useQueryClient();
  const keepCurrent = options?.keepCurrent ?? false;
  return useMutation<BulkCloseResult, ApiError, void>({
    mutationFn: () =>
      request<BulkCloseResult>(`${basePath(scope)}${keepCurrent ? '?keepCurrent=true' : ''}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionsQueryKey(scope) }),
  });
}
